import { devEnvConfig } from 'config/public';
import { mapKeys } from 'utils/collection';
import { is } from 'utils/helpers';

/** Get the Cloudflare Access headers, when credentials are configured. */
export function getCloudflareAccessHeaders(): Record<string, string> {
	const id = devEnvConfig.CF_ACCESS_CLIENT_ID;
	const secret = devEnvConfig.CF_ACCESS_CLIENT_SECRET;
	return id && secret
		? { 'CF-Access-Client-Id': id, 'CF-Access-Client-Secret': secret }
		: {};
}

/** Get an X-Forwarded-For header for the specified client IP. */
export function getForwardedForHeader(
	userIp: string | undefined,
): Record<string, string> {
	return userIp ? { 'X-Forwarded-For': userIp } : {};
}

/**
 * Get the value for the specified header.
 *
 * @example
 *
 * getHeaderValue(headers, 'Content-Type');
 * // => 'application/json'
 */
export function getHeaderValue(
	headers: HeadersInit,
	key: string,
): string | undefined {
	return is.instance(headers, Headers)
		? (headers.get(key) ?? undefined)
		: is.array(headers)
			? headers.find(([k]) => k.toLowerCase() === key.toLowerCase())?.[1]
			: (headers[key] ?? headers[key.toLowerCase()]);
}

/** Convert any headers init to a plain object with lowercase keys. */
export function toHeaderRecord(
	headers: HeadersInit | undefined,
): Record<string, string> {
	if (!headers) {
		return {};
	}

	const obj = is.instance(headers, Headers)
		? Object.fromEntries(headers.entries())
		: is.array(headers)
			? Object.fromEntries(headers)
			: headers;

	return mapKeys(obj, (val, key) => key.toLowerCase());
}

/**
 * Merge header inits into a single lowercase-keyed object, later ones winning.
 *
 * The Headers object is case insensitive, probably to match the HTTP spec
 * https://www.rfc-editor.org/rfc/rfc7230#section-3.2, but it's sort of case
 * sensitive on creation in that the same header with different case will be
 * merged instead of overwritten:
 * new Headers({ 'Content-Type': 'text/plain', 'content-type': 'text/html' });
 * => Headers { "content-type": "text/plain, text/html" }
 * Lowercasing everything before merging avoids this.
 */
export function mergeHeaders(
	...inits: (HeadersInit | undefined)[]
): Record<string, string> {
	const merged: Record<string, string> = {};
	for (const init of inits) {
		Object.assign(merged, toHeaderRecord(init));
	}
	return merged;
}
